import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import apiClient from "../../../utils/apiClient";
import { getCurrentUser } from "../../../utils/auth";

/**
 * A student's coding assignments, a page at a time: the server filters by progress
 * (`filterValues.sessionStatus`) and by the search, which is applied on Enter or when
 * the box loses focus and follows `?q=`. Starting, continuing and restarting open the
 * session. Shared by StudentAssignmentsPage and the laptop page.
 *
 * `filterValues` must be a stable object: it is a dependency of the read.
 */
const useStudentAssignments = (filterValues) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const currentUser = getCurrentUser();

  const [assignments, setAssignments] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const initialQuery = searchParams.get('q') || '';
  const [search, setSearch] = useState(initialQuery);
  const [searchInput, setSearchInput] = useState(initialQuery);
  const [actionFailed, setActionFailed] = useState(false);

  /* ⚠️ Memoised: a useEffect dependency, and a bare literal would loop. */
  const filters = useMemo(() => ({ ...filterValues, search }), [filterValues, search]);
  const [copiedCode, setCopiedCode] = useState(null);

  const fetchAssignments = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get("/assignment-sessions/list", {
        params: {
          studentId: currentUser.studentId,
          ...filters,
          page,
          limit: pageSize,
        },
      });
      setAssignments(data.assignments || []);
      setSummary(data.summary || null);
      setTotalRecords(data.total || 0);
      setFailed(false);
    } catch {
      setAssignments([]);
      setFailed(true);
    }
    setLoading(false);
  }, [filters, page, pageSize, currentUser.studentId]);

  useEffect(() => { fetchAssignments(); }, [fetchAssignments]);
  useEffect(() => { setPage(1); }, [filters]);

  useEffect(() => {
    const q = searchParams.get('q');
    if (q == null) return;
    setSearchInput(q);
    setSearch((prev) => (prev === q ? prev : q));
  }, [searchParams]);

  const handleSearchBlur = () => {
    if (searchInput !== search) setSearch(searchInput);
  };

  const handleStart = async (assignmentId) => {
    setActionFailed(false);
    try {
      const res = await apiClient.post(`/assignment-sessions/start/${assignmentId}`, {
        _id: currentUser.studentId,
      });
      navigate(`/s/assignment-session/${res.data.session._id}`);
    } catch {
      setActionFailed(true);
      console.log("Failed to start assignment");
    }
  };

  const handleRestart = async (sessionId) => {
    setActionFailed(false);
    try {
      const res = await apiClient.post(`/assignment-sessions/${sessionId}/restart`, {});
      navigate(`/s/assignment-session/${res.data.session._id}`);
    } catch {
      setActionFailed(true);
      console.log("Failed to restart assignment");
    }
  };

  const handleCopyCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  return {
    assignments,
    summary,
    loading,
    failed,
    retry: fetchAssignments,
    page,
    setPage,
    pageSize,
    totalRecords,
    search,
    searchInput,
    setSearchInput,
    handleSearchBlur,
    handleStart,
    handleRestart,
    actionFailed,
    copiedCode,
    handleCopyCode,
  };
};

export default useStudentAssignments;
